// src/components/Header.js
// Updated with notifications bell, desktop app button, reordered: desktop -> notifications -> host -> balance -> deposit
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Image, ScrollView,
  Modal, Pressable, Animated, Platform, TextInput, useWindowDimensions
} from 'react-native';
import { onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';

// Icon imports - using @expo/vector-icons (built into Expo)
import { MaterialCommunityIcons, Ionicons, FontAwesome } from '@expo/vector-icons';

import { useError } from '../components/ErrorContext';
import { getHostTier, formatHostReward } from '../constants/hostRewards';
import GoLiveButton from './GoLiveModal';
import { goLiveHeaderSlot } from '../utils/critcastLaunch';
import { isDesktopApp } from '../utils/platform';
import { useCart } from '../context/CartContext';
import { RumbleBurst } from './TsunamiMeter';
import FounderBadge from './FounderBadge';
import useCritsEnabled from '../hooks/useCritsEnabled';

const PLACEHOLDER = 'https://placehold.co/80x80/222222/888888?text=P';

// ============================================================================
// ERROR MODAL (unchanged)
// ============================================================================
const ErrorModal = () => {
    const { error, hideError } = useError();
    if (!error) return null;

    const buttons = error.buttons && error.buttons.length > 0
        ? error.buttons
        : [{ text: 'Dismiss', onPress: hideError, style: 'primary' }];

    return (
        <Modal
            visible={!!error}
            transparent={true}
            animationType="fade"
            onRequestClose={hideError}
        >
            <View style={styles.modalBackdrop}>
                <View style={styles.modalCard}>
                    <Text style={styles.modalTitle}>{error.title || 'An Error Occurred'}</Text>
                    <Text style={styles.modalMessage}>{error.message || 'Please try again later.'}</Text>
                    
                    <View style={styles.modalButtonContainer}>
                        {buttons.map((btn, index) => {
                            const isPrimary = btn.style === 'primary' || (!btn.style && index === buttons.length - 1);
                            const isCancel = btn.style === 'cancel';

                            return (
                                <Pressable
                                    key={btn.text}
                                    style={[
                                        styles.modalButton,
                                        isPrimary ? styles.modalButtonPrimary : (isCancel ? styles.modalButtonCancel : styles.modalButtonNeutral),
                                    ]}
                                    onPress={() => {
                                        hideError();
                                        if (btn.onPress) btn.onPress();
                                    }}
                                >
                                    <Text
                                        style={[
                                            styles.modalButtonText,
                                            isPrimary ? styles.modalButtonTextPrimary : styles.modalButtonTextNeutral,
                                        ]}
                                    >
                                        {btn.text}
                                    </Text>
                                </Pressable>
                            );
                        })}
                    </View>
                </View>
            </View>
        </Modal>
    );
};

// ============================================================================
// NOTIFICATIONS BELL COMPONENT
// ============================================================================
const NotificationsBell = ({ notifications, unreadCount, onPress }) => {
  const pulseAnim = useRef(new Animated.Value(1)).current;
  
  // Pulse animation when new notifications arrive
  useEffect(() => {
    if (unreadCount > 0) {
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1.2, duration: 150, useNativeDriver: true }),
        Animated.timing(pulseAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
      ]).start();
    }
  }, [unreadCount]);

  return (
    <TouchableOpacity style={styles.notificationBell} onPress={onPress} activeOpacity={0.7}>
      <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
        <Ionicons name="notifications" size={20} color="#FCD000" />
      </Animated.View>
      {unreadCount > 0 && (
        <View style={styles.notificationBadge}>
          <Text style={styles.notificationBadgeText}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

// ============================================================================
// NOTIFICATIONS DROPDOWN/MODAL
// ============================================================================

// Crit-granting notification types render the gold d20 asset (CRIT_DIE — the
// same die the Header balance and Wallet use) instead of a vector dice glyph.
const CRIT_DICE_TYPES = new Set(['crit_earned', 'crit_purchase_success', 'host_reward_earned']);

const NotificationsDropdown = ({ visible, notifications, onClose, onNotificationPress, onViewAll }) => {
  if (!visible) return null;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'match_ready': return 'gamepad-variant';
      case 'lobby_ready': return 'stadium';
      case 'lobby_regenerated': return 'refresh';
      // Live backend types (match the strings notificationService actually emits)
      case 'match_result_win': return 'trophy';
      case 'match_result_loss': return 'flag-checkered';
      case 'tournament_won': return 'crown';
      case 'round_advance': return 'arrow-up-bold-circle';
      case 'distribution_received': return 'medal';
      case 'host_reward_earned': return 'star-circle';
      case 'crit_earned': return 'dice-multiple';
      case 'crit_purchase_success': return 'dice-multiple';
      case 'subscription_active': return 'card-account-details-star';
      case 'rumble_received': return 'flash';
      case 'stream_rumble': return 'flash';
      case 'event_rumble_share': return 'flash';
      case 'rumble_refunded': return 'cash-refund';
      case 'season_started': return 'trophy-variant';
      case 'referral_pending': return 'gift-outline';
      case 'event_starting': return 'clock-outline';
      case 'event_canceled': return 'close-circle';
      case 'match_under_review': return 'shield-half-full';
      case 'dispute_filed': return 'alert';
      case 'connection_advisory': return 'wifi-alert';
      case 'sellout_certified': return 'crown';
      case 'full_crown_earned': return 'crown';
      case 'run_it_back_invite': return 'replay';
      case 'league_run_it_back_invite': return 'replay';
      case 'stream_live': return 'video';
      case 'stream_new_subscriber': return 'account-plus';
      // Channel memberships (creator side, then the member's own notices)
      case 'stream_new_member': return 'account-star';
      case 'membership_creator_status': return 'account-alert';
      // Team memberships (captain side; the member's own notices use the types below)
      case 'membership_team_new_member': return 'account-group';
      case 'membership_team_status': return 'account-alert';
      case 'membership_active': return 'star-circle';
      case 'membership_payment_failed': return 'credit-card-remove';
      case 'membership_notice': return 'information-outline';
      // Paid Plan billing notices
      case 'plan_renewal_reminder': return 'calendar-refresh';
      case 'subscription_payment_failed': return 'credit-card-remove';
      // Legacy/demo aliases kept harmless
      case 'match_result': return 'trophy';
      case 'payout': return 'cash';
      default: return 'bell';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.dropdownOverlay} onPress={onClose}>
        <Pressable style={styles.dropdownContainer} onPress={(e) => e.stopPropagation()}>
          {/* Header */}
          <View style={styles.dropdownHeader}>
            <Text style={styles.dropdownTitle}>Notifications</Text>
            <TouchableOpacity onPress={onClose}>
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>

          {/* Notifications List */}
          <ScrollView style={styles.dropdownList} showsVerticalScrollIndicator={false}>
            {notifications.length === 0 ? (
              <View style={styles.emptyNotifications}>
                <Ionicons name="notifications-off-outline" size={40} color="#444" style={styles.emptyIcon} />
                <Text style={styles.emptyText}>No notifications yet</Text>
                <Text style={styles.emptySubtext}>You'll see go-live alerts, match alerts and updates here</Text>
              </View>
            ) : (
              notifications.map((notif, index) => (
                <TouchableOpacity
                  key={notif.id || index}
                  style={[
                    styles.notificationItem,
                    !notif.read && styles.notificationItemUnread
                  ]}
                  onPress={() => onNotificationPress(notif)}
                  activeOpacity={0.7}
                >
                  {CRIT_DICE_TYPES.has(notif.type) ? (
                    <Image source={CRIT_DIE} style={[styles.notificationItemIcon, { width: 24, height: 24 }]} resizeMode="contain" />
                  ) : (
                    <MaterialCommunityIcons name={getNotificationIcon(notif.type)} size={24} color="#FCD000" style={styles.notificationItemIcon} />
                  )}
                  <View style={styles.notificationItemContent}>
                    <Text style={[
                      styles.notificationItemTitle,
                      !notif.read && styles.notificationItemTitleUnread
                    ]}>
                      {notif.title}
                    </Text>
                    <Text style={styles.notificationItemBody} numberOfLines={2}>
                      {notif.body}
                    </Text>
                    <Text style={styles.notificationItemTime}>
                      {formatTime(notif.createdAt)}
                    </Text>
                  </View>
                  {!notif.read && <View style={styles.unreadDot} />}
                </TouchableOpacity>
              ))
            )}
          </ScrollView>

          {/* Footer */}
          {notifications.length > 0 && (
            <TouchableOpacity style={styles.dropdownFooter} onPress={onViewAll}>
              <Text style={styles.dropdownFooterText}>View All Notifications</Text>
            </TouchableOpacity>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// ============================================================================
// USER SEARCH DROPDOWN (2026-07-14) — find a player by username and jump to
// their public profile (/u/:userName). Queries the world-readable
// public_profiles collection with lowercase prefix queries (userNameLower is
// written at signup and backfilled; the raw-userName query covers any doc the
// backfill hasn't touched). Debounced so we don't fire reads per keystroke.
// ============================================================================
const UserSearchDropdown = ({ visible, onClose, onSelect }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);   // null = idle · [] = no hits
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!visible) { setQ(''); setResults(null); setSearching(false); }
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const term = q.trim().toLowerCase();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (term.length < 2) { setResults(null); setSearching(false); return undefined; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const col = collection(db, 'public_profiles');
        const end = term + '';
        const [byLower, byName] = await Promise.all([
          getDocs(query(col, where('userNameLower', '>=', term), where('userNameLower', '<=', end), limit(8))),
          getDocs(query(col, where('userName', '>=', term), where('userName', '<=', end), limit(8))),
        ]);
        const seen = new Map();
        [...byLower.docs, ...byName.docs].forEach((d) => {
          const p = d.data() || {};
          if (p.userName && !seen.has(d.id)) {
            seen.set(d.id, { uid: d.id, userName: p.userName, photo: p.profilePhotoUrl || null });
          }
        });
        setResults(Array.from(seen.values()).slice(0, 8));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, visible]);

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.searchOverlay} onPress={onClose}>
        <Pressable style={styles.dropdownContainer} onPress={(e) => e.stopPropagation()}>
          <View style={styles.dropdownHeader}>
            <Text style={styles.dropdownTitle}>Find Players</Text>
            <TouchableOpacity onPress={onClose}>
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>

          <View style={styles.searchInputRow}>
            <Ionicons name="search" size={16} color="#666" style={{ marginRight: 8 }} />
            <TextInput
              style={styles.searchInput}
              value={q}
              onChangeText={setQ}
              placeholder="Search by username…"
              placeholderTextColor="#555"
              autoCapitalize="none"
              autoCorrect={false}
              autoFocus
            />
          </View>

          <ScrollView style={styles.dropdownList} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            {searching ? (
              <View style={styles.emptyNotifications}>
                <Text style={styles.emptyText}>Searching…</Text>
              </View>
            ) : results === null ? (
              <View style={styles.emptyNotifications}>
                <Ionicons name="people-outline" size={40} color="#444" style={styles.emptyIcon} />
                <Text style={styles.emptyText}>Find other players</Text>
                <Text style={styles.emptySubtext}>Type at least 2 letters of a username</Text>
              </View>
            ) : results.length === 0 ? (
              <View style={styles.emptyNotifications}>
                <Text style={styles.emptyText}>No players found</Text>
                <Text style={styles.emptySubtext}>Usernames match from the first letters</Text>
              </View>
            ) : (
              results.map((r) => (
                <TouchableOpacity
                  key={r.uid}
                  style={styles.searchResultRow}
                  onPress={() => onSelect(r.userName)}
                  activeOpacity={0.7}
                >
                  {r.photo ? (
                    <Image source={{ uri: r.photo }} style={styles.searchResultAvatar} />
                  ) : (
                    <View style={[styles.searchResultAvatar, styles.searchResultAvatarEmpty]}>
                      <Text style={styles.searchResultInitial}>{String(r.userName).slice(0, 1).toUpperCase()}</Text>
                    </View>
                  )}
                  <Text style={styles.searchResultName} numberOfLines={1}>{r.userName}</Text>
                  <Ionicons name="chevron-forward" size={16} color="#555" />
                </TouchableOpacity>
              ))
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// ============================================================================
// CART ICON + ITEM-COUNT BADGE
// ============================================================================
// Shows the cart bag icon in the header's right section. Tap → navigate to
// the Cart screen. Item-count badge appears when items > 0 (hidden when
// empty — keeps the chrome quiet for the 99% of sessions that aren't
// mid-purchase, and the badge appearance gives a satisfying "you added a
// thing" confirmation). Visible to both guest and signed-in users since the
// cart is device-local — guests can build a cart and the checkout flow
// gates them at MerchCheckoutScreen.
const CartIcon = ({ count, onPress }) => (
  <TouchableOpacity style={styles.cartIcon} onPress={onPress} activeOpacity={0.7}>
    <Ionicons name="bag-handle-outline" size={20} color="#FCD000" />
    {count > 0 && (
      <View style={styles.cartBadge}>
        <Text style={styles.cartBadgeText}>{count > 99 ? '99+' : count}</Text>
      </View>
    )}
  </TouchableOpacity>
);

// ============================================================================
// COMPACT HOST INDICATOR
// ============================================================================
const CompactHostIndicator = ({ hostData, navigation }) => {
  if (!hostData || !hostData.isHostOptIn) return null;

  const tier = getHostTier(hostData.hostStats?.eventsCompletedAsHost || 0);
  const pendingCents = hostData.wallet?.credits?.hostingPending || 0;
  const hasPending = pendingCents > 0;

  return (
    <TouchableOpacity 
      style={styles.hostIndicator}
      onPress={() => navigation.navigate('HostScreen')}
      activeOpacity={0.7}
    >
      <View style={[styles.hostBadge, { borderColor: tier.color }]}>
        <FontAwesome name="star" size={12} color={tier.color} />
      </View>
      <View style={styles.hostInfo}>
        <Text style={styles.hostLabel}>HOST</Text>
        {hasPending && (
          <Text style={styles.hostPending}>+{formatHostReward(pendingCents / 100)}</Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

// ============================================================================
// MAIN HEADER COMPONENT
// ============================================================================
// ----------------------------------------------------------------------------
// Header stat chips — Crits (gold d20 backdrop) + Earnings. Both route to the
// Wallet (Crits & Earnings) screen; hover shifts gold → cyan on web.
// "BALANCE" became "EARNINGS" post-pivot: with deposits disabled, the cash
// balance only fills from creator comp — it IS take-home earnings.
// ----------------------------------------------------------------------------
const CRIT_DIE = require('../../assets/20_pdice.webp');
// Color = the Crit-flavor differentiator (no rename): GREEN die = ENTRY Crits
// (paid — membership + packs, persist); PURPLE die = EARNED Crits (won through
// play, reset each season). Used consistently wherever the split is shown.
const ENTRY_DIE = require('../../assets/20_gdice.webp');
const EARNED_DIE = require('../../assets/20_pdice.webp');

const HeaderStatChip = ({ label, value, showDie, onPress }) => {
  const [hovered, setHovered] = useState(false);
  const hoverProps = Platform.OS === 'web'
    ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
    : {};
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.75}
      style={[styles.statChip, hovered && styles.statChipHover]}
      {...hoverProps}
    >
      {showDie && (
        <Image
          source={CRIT_DIE}
          style={[styles.statChipDie, hovered && styles.statChipDieHover]}
          resizeMode="contain"
        />
      )}
      <View style={styles.statChipInner}>
        <Text style={[styles.statChipLabel, hovered && styles.statChipTextHover]}>{label}</Text>
        <Text style={[styles.statChipValue, hovered && styles.statChipTextHover]}>{value}</Text>
      </View>
    </TouchableOpacity>
  );
};

// Crits chip split into ENTRY (paid — membership + packs, persists) and EARNED
// (won through play, resets each season). Two mini-columns in one chip so the
// header stays compact. Both open the Wallet.
const CritSplitChip = ({ entry, earned, onPress }) => {
  const [hovered, setHovered] = useState(false);
  const hoverProps = Platform.OS === 'web'
    ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
    : {};
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.75}
      style={[styles.statChip, hovered && styles.statChipHover]}
      {...hoverProps}
    >
      <View style={styles.critSplitRow}>
        <View style={styles.critSplitCol}>
          <View style={styles.critSplitLabelRow}>
            <Image source={ENTRY_DIE} style={styles.critSplitDie} resizeMode="contain" />
            <Text style={[styles.statChipLabel, hovered && styles.statChipTextHover]}>ENTRY</Text>
          </View>
          <Text style={[styles.statChipValue, hovered && styles.statChipTextHover]}>{entry}</Text>
        </View>
        <View style={styles.critSplitDivider} />
        <View style={styles.critSplitCol}>
          <View style={styles.critSplitLabelRow}>
            <Image source={EARNED_DIE} style={styles.critSplitDie} resizeMode="contain" />
            <Text style={[styles.statChipLabel, hovered && styles.statChipTextHover]}>EARNED</Text>
          </View>
          <Text style={[styles.statChipValue, hovered && styles.statChipTextHover]}>{earned}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default function Header({ navigation }) {
  // Go Live pill slot (S7.6): first in the right section, or last on phones
  // under 360 px so the guest LOGIN button keeps its place on screen.
  const { width: windowWidth } = useWindowDimensions();
  const goLiveSlot = goLiveHeaderSlot(windowWidth);

  // Auth + user/profile/wallet
  const [u, setU] = useState(null);
  const [pub, setPub] = useState(null);
  const [priv, setPriv] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [hostData, setHostData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminOnlyEvents, setAdminOnlyEvents] = useState(true);
  const [hideLeagues, setHideLeagues] = useState(true);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [critBurst, setCritBurst] = useState(null); // { title, subtitle } — dice rain when Crits are earned
  const seenFirstNotifSnap = useRef(false);
  // Crit economy flag (settings/economy.critsEnabled). The notifications
  // listener below is registered once inside a []-deps effect, so it reads
  // the ref (kept fresh by the effect) rather than the state variable.
  const critsEnabled = useCritsEnabled();
  const critsEnabledRef = useRef(true);
  useEffect(() => { critsEnabledRef.current = critsEnabled; }, [critsEnabled]);

  // Desktop client detection
  const inDesktopClient = isDesktopApp();

  // Cart item count for the header bag badge
  const { itemCount: cartItemCount } = useCart();
  
  // Real-time listener for event creation config (global platform setting)
  useEffect(() => {
    const unsubEventConfig = onSnapshot(
      doc(db, 'settings', 'events'),
      (snap) => {
        if (snap.exists()) {
          setAdminOnlyEvents(snap.data().adminOnlyEventCreation ?? true);
          setHideLeagues(!!snap.data().hideLeagues);
        } else {
          setAdminOnlyEvents(true); // Default: locked down
          setHideLeagues(false);
        }
      },
      () => setAdminOnlyEvents(true)
    );
    return () => unsubEventConfig();
  }, []);

  const unsubPub = useRef(null), unsubPriv = useRef(null), unsubWallet = useRef(null), unsubHost = useRef(null), unsubNotifs = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setU(user || null);
      unsubPub.current?.(); unsubPriv.current?.(); unsubWallet.current?.(); unsubHost.current?.(); unsubNotifs.current?.();
      if (!user) { 
        setPub(null); setPriv(null); setWallet(null); setHostData(null); setIsAdmin(false); setNotifications([]);
        return; 
      }

      unsubPub.current = onSnapshot(doc(db, 'public_profiles', user.uid),
        s => setPub(s.exists() ? s.data() : null),
        () => setPub(null)
      );
      
      unsubPriv.current = onSnapshot(doc(db, 'users', user.uid),
        s => { 
          const d = s.exists() ? s.data() : null; 
          setPriv(d); 
          if (d?.role === 'admin') setIsAdmin(true); 
        },
        () => setPriv(null)
      );
      
      unsubWallet.current = onSnapshot(doc(db, 'wallets', user.uid),
        s => setWallet(s.exists() ? s.data() : null),
        () => setWallet(null)
      );

      // Listen to host data
      unsubHost.current = onSnapshot(doc(db, 'hosts', user.uid),
        s => {
          if (s.exists()) {
            setHostData(s.data());
          } else {
            setHostData({
              isHostOptIn: false,
              hostTier: 1,
              hostStats: { eventsCompletedAsHost: 0 },
              wallet: { credits: { hostingPending: 0, hostingTotal: 0 } }
            });
          }
        },
        () => setHostData(null)
      );

      // Listen to notifications (most recent 20)
      const notifsQuery = query(
        collection(db, 'users', user.uid, 'notifications'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      seenFirstNotifSnap.current = false;
      unsubNotifs.current = onSnapshot(notifsQuery,
        snapshot => {
          const notifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setNotifications(notifs);
          // Rain the King Crit dice when a NEW Crit-earning notification arrives
          // (crit_earned or host_reward_earned) and only while the Crit economy
          // is enabled. Skip the initial load so existing notifications don't
          // trigger it (the seenFirstNotifSnap + 'added' guard limits this to
          // genuinely new docs, so a resync doesn't re-rain old Crits).
          if (seenFirstNotifSnap.current) {
            snapshot.docChanges().forEach(ch => {
              if (ch.type !== 'added') return;
              const n = ch.doc.data() || {};
              const amt = Number(n.crits || 0);
              const isCritEarn = critsEnabledRef.current
                && (n.type === 'crit_earned' || n.type === 'host_reward_earned');
              if (!isCritEarn) return;
              // Only celebrate genuinely-fresh Crits. If the listener re-adds an
              // old notification on reconnect / refocus, its server createdAt is
              // already set and old — skip the rain. A brand-new doc's createdAt
              // may still be pending (null) under latency compensation, so a
              // missing timestamp is treated as fresh (allow).
              const createdMs = n.createdAt?.toDate ? n.createdAt.toDate().getTime() : 0;
              if (createdMs && (Date.now() - createdMs) > 120000) return;
              setCritBurst({
                title: 'Crits earned!',
                subtitle: amt > 0 ? `+${amt} Crit${amt === 1 ? '' : 's'} earned` : (n.title || 'You earned Crits'),
              });
            });
          } else {
            seenFirstNotifSnap.current = true;
          }
        },
        () => setNotifications([])
      );

      try {
        const token = await getIdTokenResult(user, true);
        if (token?.claims?.role === 'admin') setIsAdmin(true);
      } catch {}
    });
    return () => { 
      unsub?.(); 
      unsubPub.current?.(); 
      unsubPriv.current?.(); 
      unsubWallet.current?.(); 
      unsubHost.current?.(); 
      unsubNotifs.current?.();
    };
  }, []);

  const merged = useMemo(() => {
    const name = pub?.userName || u?.displayName || 'Player';
    const photo = pub?.profilePhotoUrl || u?.photoURL || PLACEHOLDER;
    const cents = Number.isFinite(wallet?.balanceCents)
      ? wallet.balanceCents
      : (Number.isFinite(priv?.balanceCents)
        ? priv.balanceCents
        : Math.round((Number(priv?.balance || 0)) * 100));
    const crits = Math.max(0, Math.floor(Number(priv?.critBalance || 0)));
    // Split for the header: EARNED (won through play, resets each season) vs
    // ENTRY (paid — membership + packs, persists). Legacy docs without buckets
    // read as all-entry. critBalance = entry + earned.
    const earnedCrits = Math.max(0, Math.floor(Number(priv?.earnedCritBalance || 0)));
    const entryCrits = priv?.purchasedCritBalance != null
      ? Math.max(0, Math.floor(Number(priv.purchasedCritBalance)))
      : Math.max(0, crits - earnedCrits);
    return { name, photo, balance: Math.max(0, cents) / 100, crits, entryCrits, earnedCrits, profileBadge: pub?.profileBadge || 'none' };
  }, [u, pub, priv, wallet]);

  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  const isLoggedIn = !!u;
  const activeRouteName =
    navigation?.getState?.()?.routes?.[navigation.getState().index]?.name || '';

  const safeGo = (name, params, fallbacks = []) => {
    try { navigation.navigate(name, params); return; } catch {}
    for (const fb of fallbacks) { try { navigation.navigate(fb, params); return; } catch {} }
  };
  
  const goProfile = () => safeGo(isLoggedIn ? 'Profile' : 'Login');
  const goDeposit = () => safeGo(isLoggedIn ? 'Wallet' : 'Login');

  const handleNotificationPress = async (notification) => {
    setShowNotifications(false);
    
    // Mark as read
    if (!notification.read && u) {
      const { updateDoc, doc } = await import('firebase/firestore');
      try {
        await updateDoc(doc(db, 'users', u.uid, 'notifications', notification.id), { read: true });
      } catch (e) {
        console.error('Failed to mark notification as read:', e);
      }
    }
    
    // Celebrate again on tap for a Crit-earning notification (flag on only).
    const critAmt = Number(notification.crits || 0);
    const isCritEarn = critsEnabled
      && (notification.type === 'crit_earned' || notification.type === 'host_reward_earned');
    if (isCritEarn) {
      setCritBurst({
        title: 'Crits earned!',
        subtitle: critAmt > 0 ? `+${critAmt} Crit${critAmt === 1 ? '' : 's'} earned` : 'You earned Crits',
      });
    }

    // Navigate based on notification type (match the strings the backend emits).
    const type = notification.type;
    if (type === 'stream_live') {
      // Payload is { streamerUid }; the watch page resolves a uid as a channel.
      if (notification.streamerUid) safeGo('StreamWatch', { channel: notification.streamerUid });
    } else if (type === 'stream_new_subscriber') {
      safeGo('Streams');
    } else if (type === 'stream_rumble') {
      // A squad Rumble opens that squad stream; a solo stream Rumble opens the
      // creator's own metrics. (Notification data is stored at the top level.)
      if (notification.targetType === 'team' && notification.targetId) safeGo('TeamStream', { teamStreamId: notification.targetId });
      else safeGo('CreatorMetrics');
    } else if (type === 'match_ready' || type === 'lobby_ready' || type === 'lobby_regenerated' || type === 'round_advance') {
      safeGo('MatchLobby', {
        matchId: notification.matchId,
        eventId: notification.eventId,
      });
    } else if (
      type === 'match_result_win' || type === 'match_result_loss' ||
      type === 'tournament_won' || type === 'distribution_received' ||
      type === 'host_reward_earned' || type === 'crit_earned' ||
      type === 'rumble_received' ||
      type === 'event_rumble_share' || type === 'rumble_refunded' ||
      type === 'event_starting' || type === 'event_canceled' ||
      type === 'run_it_back_invite' ||
      type === 'match_result' // legacy/demo alias
    ) {
      // Registered route is 'EventDetailScreen' ('EventDetail' is not a route).
      if (notification.eventId) safeGo('EventDetailScreen', { eventId: notification.eventId }, ['EventDetail']);
    } else if (type === 'sellout_certified' || type === 'full_crown_earned') {
      // Certification lands on whichever surface it belongs to (event or league).
      if (notification.eventId) safeGo('EventDetailScreen', { eventId: notification.eventId }, ['EventDetail']);
      else if (notification.leagueId) safeGo('LeagueDetail', { leagueId: notification.leagueId });
    } else if (type === 'league_run_it_back_invite') {
      if (notification.leagueId) safeGo('LeagueDetail', { leagueId: notification.leagueId });
    } else if (type === 'membership_team_new_member' || type === 'membership_team_status') {
      // Captain notices carry the team at the top level (targetType 'team',
      // targetId). Without one, the screen falls back to the viewer's own team.
      safeGo('CreatorTeamMemberships', notification.targetId ? { teamId: notification.targetId } : undefined);
    } else if (type === 'stream_new_member' || type === 'membership_creator_status') {
      safeGo('CreatorMemberships');
    } else if (
      type === 'membership_active' || type === 'membership_payment_failed' ||
      type === 'membership_notice'
    ) {
      safeGo('Memberships');
    } else if (
      type === 'subscription_active' || type === 'crit_purchase_success' ||
      type === 'plan_renewal_reminder' || type === 'subscription_payment_failed'
    ) {
      safeGo('Subscription');
    } else if (type === 'season_started') {
      safeGo('Leaderboard');
    } else if (type === 'referral_pending') {
      safeGo('Profile');
    } else if (type === 'payout') {
      // Legacy alias (no live emitter) — cash earnings surface.
      safeGo('Wallet');
    }
  };

  // Hide "Create Event" for non-admins when admin-only event creation is enabled
  const showCreateEvent = isAdmin || !adminOnlyEvents;
  // League nav mirrors the Lobby's soft-hide (settings/events.hideLeagues).
  const showLeagueNav = isAdmin || !hideLeagues;

  // League button: your league if you're in exactly one (solo entrant, team
  // captain, or squad member), the My Leagues hub if you're in several
  // (multi-league is supported — 5 free slots, Crit-unlockable beyond),
  // league creation if you're in none.
  const goLeague = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) { safeGo('SignUp'); return; }
    try {
      const snap = await getDocs(query(
        collection(db, 'leagues'), where('status', 'in', ['registration', 'active'])));
      const mine = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((lg) => (lg.rosterUids || []).includes(uid)
          || Object.values(lg.roster || {}).some((info) =>
            (info?.lineupUids || []).includes(uid)
            || (info?.paidUids || []).includes(uid)
            || (info?.teamRosterUids || []).includes(uid)
            || (info?.squadUids || []).includes(uid)));
      if (mine.length > 1) safeGo('MyLeagues');
      else if (mine.length === 1) safeGo('LeagueDetail', { leagueId: mine[0].id });
      else safeGo('CreateLeague');
    } catch {
      safeGo('CreateLeague');
    }
  };

  const navItems = [
    { key: 'Home',    label: 'Home',           to: 'Home' },
    { key: 'Lobby',   label: 'Events',         to: 'Lobby' },
    { key: 'Live',    label: 'Live Matches',   to: 'LiveMatches' },
    { key: 'Streams', label: 'Streamers',      to: 'Streams' },
    { key: 'Leaders', label: 'Leaderboard',    to: 'Leaderboard' },
    { key: 'Yours',   label: 'Your Matches',   to: 'PendingMatches', params: { onlyMine: true }, requiresAuth: true },
    { key: 'Team',    label: 'Your Team',      to: 'YourTeam', requiresAuth: true },
    ...(showCreateEvent ? [{ key: 'Create', label: isAdmin ? 'Create Official' : 'Create Event', to: isAdmin ? 'CreateOfficialTournament' : 'CreateTournament', requiresAuth: true }] : []),
    ...(showLeagueNav ? [{ key: 'League', label: 'League', onPress: goLeague, requiresAuth: true, activeFor: ['CreateLeague', 'LeagueDetail', 'LeagueCommandCenter', 'MyLeagues'] }] : []),
    { key: 'How',     label: 'How To Play',    to: 'HowToPlay' },
    { key: 'Store',   label: 'Store',       to: 'Store' },
    // { key: 'Library', label: 'Library',     to: 'GameLibrary' }, // Hidden 2026-05-26 — restore when desktop + Steam + vision integration ships
  ];

  // Guest-aware navigation: protected nav items (Create Event, Your Matches,
  // Your Team) route a not-signed-in user to SignUp instead of letting them
  // dead-end at a cloud function. Public nav (Lobby, Live, How, Store) routes
  // normally for everyone. Added 2026-05-30 pre-launch guest UX audit.
  const NavButton = ({ label, to, params, requiresAuth, onPress, activeFor }) => {
    const active = activeFor ? activeFor.includes(activeRouteName) : activeRouteName === to;
    const handlePress = () => {
      if (requiresAuth && !isLoggedIn) {
        safeGo('SignUp');
        return;
      }
      if (onPress) { onPress(); return; }
      safeGo(to, params);
    };
    return (
      <TouchableOpacity
        onPress={handlePress}
        style={[styles.actionBtn, active && styles.actionBtnActive]}
      >
        <Text style={[styles.actionBtnText, active && styles.actionBtnTextActive]}>{label}</Text>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.headerWrap}>
      <ErrorModal />
      {/* Dice rain when Crits are earned from an event (same burst as Rumble). */}
      <RumbleBurst
        visible={!!critBurst}
        title={critBurst?.title}
        subtitle={critBurst?.subtitle}
        onDone={() => setCritBurst(null)}
      />
      <NotificationsDropdown
        visible={showNotifications}
        notifications={notifications}
        onClose={() => setShowNotifications(false)}
        onNotificationPress={handleNotificationPress}
        onViewAll={() => {
          setShowNotifications(false);
          safeGo('Notifications');
        }}
      />
      <UserSearchDropdown
        visible={showUserSearch}
        onClose={() => setShowUserSearch(false)}
        onSelect={(userName) => {
          setShowUserSearch(false);
          safeGo('PublicProfile', { userName });
        }}
      />

      {/* Row 1: brand + desktop app + notifications + host + balance + deposit */}
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false} 
        style={styles.topRowScroll}
        contentContainerStyle={styles.topRow}
      >
        <TouchableOpacity style={styles.profileContainer} onPress={goProfile}>
          <View style={{ marginRight: 10 }}>
            <FounderBadge size={44} box={44} active={isLoggedIn && merged.profileBadge === 'founder'}>
              <Image source={{ uri: merged.photo }} style={styles.avatar} />
            </FounderBadge>
          </View>
          <View>
            <Text style={styles.brand}>CritCash</Text>
            <Text style={styles.userName}>{isLoggedIn ? merged.name : 'Guest'}</Text>
          </View>
        </TouchableOpacity>

        <View style={styles.rightSection}>
          {/* Go Live (P14, design S7.6): the gold pill for every visitor,
              logged out included. On the web it opens the GoLiveModal; inside
              the CritCast app it expands the Broadcast panel. Under 360 px it
              renders at the end of this row instead (goLiveHeaderSlot). */}
          {goLiveSlot === 'first' && <GoLiveButton navigation={navigation} isLoggedIn={isLoggedIn} />}

          {/* Cart icon — visible to everyone (cart is device-local; the
              checkout flow gates guests). Badge appears when items > 0 so
              the chrome stays quiet for sessions that aren't mid-purchase
              and the badge appearance signals "added to cart" naturally. */}
          <CartIcon
            count={cartItemCount}
            onPress={() => safeGo('Cart')}
          />

          {/* Notifications Bell - Only when logged in */}
          {isLoggedIn && (
            <NotificationsBell
              notifications={notifications}
              unreadCount={unreadCount}
              onPress={() => setShowNotifications(true)}
            />
          )}

          {/* Host Indicator - Only when logged in and opted in */}
          {isLoggedIn && hostData?.isHostOptIn && (
            <CompactHostIndicator hostData={hostData} navigation={navigation} />
          )}

          {/* Crits + Earnings chips — replace the old BALANCE readout +
              WALLET button. Both open the Wallet (Crits & Earnings) screen. */}
          {isLoggedIn && critsEnabled && (
            <CritSplitChip
              entry={merged.entryCrits}
              earned={merged.earnedCrits}
              onPress={goDeposit}
            />
          )}
          {isLoggedIn && (
            <HeaderStatChip
              label="EARNINGS"
              value={`$${merged.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              onPress={goDeposit}
            />
          )}

          {/* Login Button (guests only) */}
          {!isLoggedIn && (
            <TouchableOpacity onPress={goDeposit} style={styles.depositBtn}>
              <Text style={styles.depositBtnText}>LOGIN</Text>
            </TouchableOpacity>
          )}

          {/* Go Live, phones under 360 px: after LOGIN and the wallet chips. */}
          {goLiveSlot === 'last' && <GoLiveButton navigation={navigation} isLoggedIn={isLoggedIn} />}
        </View>
      </ScrollView>

      {/* Row 2: primary actions */}
      <View style={styles.actionRow}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.actionsScroll}>
          {navItems.map(n => <NavButton key={n.key} {...n} />)}
          {/* Player search — magnifying glass next to Store (founder 2026-07-14) */}
          <TouchableOpacity
            onPress={() => setShowUserSearch(true)}
            style={styles.actionBtn}
            accessibilityLabel="Search players"
          >
            <Ionicons name="search" size={15} color="#FCD000" />
          </TouchableOpacity>
        </ScrollView>
      </View>
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  headerWrap: { backgroundColor: '#0b0b0c', borderBottomWidth: 1, borderBottomColor: '#222', width: '100%', overflow: 'hidden' },
  topRowScroll: { width: '100%' },
  topRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 10, minWidth: '100%' },
  profileContainer: { flexDirection: 'row', alignItems: 'center' },
  avatar: { width: 44, height: 44, borderRadius: 22, backgroundColor: '#222' },
  brand: { color: '#FCD000', fontWeight: '900', fontSize: 14, letterSpacing: 0.5, lineHeight: 16 },
  userName: { color: '#fff', fontWeight: '700', fontSize: 14, marginTop: 1 },
  
  // Right section layout
  rightSection: { 
    flexDirection: 'row', 
    alignItems: 'center',
    gap: 12,
    marginLeft: 'auto',
    paddingLeft: 12,
  },
  
  // Cart Icon — mirrors notificationBell layout so the chrome reads as one
  // consistent row of action chips. Badge style mirrors notificationBadge.
  cartIcon: {
    position: 'relative',
    padding: 8,
    backgroundColor: '#141414',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  cartBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#FCD000',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: '#0b0b0c',
  },
  cartBadgeText: {
    color: '#0A0A0A',
    fontSize: 10,
    fontWeight: '900',
  },

  // Notifications Bell
  notificationBell: {
    position: 'relative',
    padding: 8,
    backgroundColor: '#141414',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  bellIcon: {
    fontSize: 18,
  },
  notificationBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#FF4444',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: '#0b0b0c',
  },
  notificationBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
  },

  // Notifications Dropdown
  dropdownOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingTop: 70,
    paddingRight: 12,
  },
  searchOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: 70,
    paddingHorizontal: 12,
  },
  searchInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
    marginVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: '#111',
    borderWidth: 1,
    borderColor: '#2a2a2a',
    borderRadius: 10,
  },
  searchInput: {
    flex: 1,
    color: '#fff',
    fontSize: 14,
    paddingVertical: 10,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : null),
  },
  searchResultRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  searchResultAvatar: { width: 34, height: 34, borderRadius: 17, backgroundColor: '#222' },
  searchResultAvatarEmpty: { alignItems: 'center', justifyContent: 'center', backgroundColor: '#2a2a2a' },
  searchResultInitial: { color: '#FCD000', fontSize: 15, fontWeight: '900' },
  searchResultName: { flex: 1, color: '#eee', fontSize: 14, fontWeight: '700' },
  dropdownContainer: {
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    width: 340,
    maxWidth: '90%',
    maxHeight: 480,
    borderWidth: 1,
    borderColor: '#2a2a2a',
    overflow: 'hidden',
  },
  dropdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#2a2a2a',
  },
  dropdownTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '800',
  },
  dropdownClose: {
    color: '#666',
    fontSize: 20,
    padding: 4,
  },
  dropdownList: {
    maxHeight: 360,
  },
  emptyNotifications: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  emptyIcon: {
    marginBottom: 12,
    opacity: 0.3,
  },
  emptyText: {
    color: '#888',
    fontSize: 16,
    fontWeight: '600',
  },
  emptySubtext: {
    color: '#555',
    fontSize: 13,
    marginTop: 4,
  },
  notificationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  notificationItemUnread: {
    backgroundColor: '#1f1f1f',
  },
  notificationItemIcon: {
    marginRight: 12,
    marginTop: 2,
  },
  notificationItemContent: {
    flex: 1,
  },
  notificationItemTitle: {
    color: '#ccc',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  notificationItemTitleUnread: {
    color: '#fff',
  },
  notificationItemBody: {
    color: '#888',
    fontSize: 13,
    lineHeight: 18,
  },
  notificationItemTime: {
    color: '#555',
    fontSize: 11,
    marginTop: 6,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FCD000',
    marginLeft: 8,
    marginTop: 6,
  },
  dropdownFooter: {
    padding: 14,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#2a2a2a',
  },
  dropdownFooterText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Balance
  balanceContainer: { alignItems: 'flex-end' },
  balanceLabel: { color: '#8a8a8a', fontSize: 10, fontWeight: '700' },
  balanceAmount: { color: '#fff', fontSize: 16, fontWeight: '800' },

  // Crits / Earnings stat chips
  statChip: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    minWidth: 68,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2a2a2a',
    backgroundColor: '#141414',
    overflow: 'hidden',
  },
  statChipHover: {
    borderColor: '#00E5FF',
    backgroundColor: '#101a1d',
  },
  statChipDie: {
    position: 'absolute',
    width: 40,
    height: 40,
    opacity: 0.4,
  },
  statChipDieHover: {
    opacity: 0.75,
  },
  statChipInner: { alignItems: 'center' },
  statChipLabel: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
    textShadowColor: 'rgba(0,0,0,0.9)',
    textShadowRadius: 3,
  },
  statChipValue: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.9)',
    textShadowRadius: 3,
  },
  statChipTextHover: {
    color: '#00E5FF',
  },
  critSplitRow: { flexDirection: 'row', alignItems: 'center' },
  critSplitCol: { alignItems: 'center', minWidth: 40 },
  critSplitLabelRow: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  critSplitDie: { width: 14, height: 14 },
  critSplitDivider: { width: 1, alignSelf: 'stretch', backgroundColor: 'rgba(255,255,255,0.18)', marginHorizontal: 7, marginVertical: 3 },

  // Compact Host Indicator Styles
  hostIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#141414',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2a2a2a',
    gap: 8,
  },
  hostBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0a0a0a',
  },
  hostBadgeIcon: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  hostInfo: {
    alignItems: 'flex-start',
  },
  hostLabel: {
    color: '#8a8a8a',
    fontSize: 8,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  hostPending: {
    color: '#FCD000',
    fontSize: 11,
    fontWeight: '800',
    marginTop: 1,
  },
  
  // Deposit Button
  depositBtn: { backgroundColor: '#00A86B', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  depositBtnText: { color: '#fff', fontWeight: '900', fontSize: 12 },
  
  // Action Row
  actionRow: { borderTopWidth: 1, borderTopColor: '#141414', paddingHorizontal: 8, paddingVertical: 10, width: '100%', overflow: 'hidden' },
  actionsScroll: { paddingHorizontal: 6, gap: 8, alignItems: 'center' },
  actionBtn: { borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#121212', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10 },
  actionBtnActive: { borderColor: '#FCD000' },
  actionBtnText: { color: '#eee', fontWeight: '800', fontSize: 12 },
  actionBtnTextActive: { color: '#FCD000' },

  // Error Modal Styles (unchanged)
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.6)', justifyContent: 'center', alignItems: 'center' },
  modalCard: { backgroundColor: '#1a1a1a', borderRadius: 12, padding: 20, width: '85%', maxWidth: 350, borderWidth: 1, borderColor: '#333' },
  modalTitle: { color: '#fff', fontSize: 18, fontWeight: 'bold', marginBottom: 10 },
  modalMessage: { color: '#ccc', fontSize: 14, marginBottom: 20, lineHeight: 20 },
  modalButtonContainer: { flexDirection: 'column-reverse', gap: 8 },
  modalButton: { borderRadius: 8, paddingVertical: 12, alignItems: 'center' },
  modalButtonPrimary: { backgroundColor: '#FCD000' },
  modalButtonNeutral: { backgroundColor: '#333' },
  modalButtonCancel: { backgroundColor: 'transparent' },
  modalButtonText: { fontWeight: 'bold', fontSize: 14 },
  modalButtonTextPrimary: { color: '#000' },
  modalButtonTextNeutral: { color: '#fff' },
});